import { BetType, BetTypeKey } from '../../models/bet-type.model';
import { Match } from '../../models/match.model';
import { Odd } from '../../models/odd.model';
import { Sport } from '../../models/sport.model';
import { MATCH_DETAIL_SECTION_FAMILIES, MATCH_DETAIL_TAB_DEFINITIONS } from '../config/sportsbook-match-detail.config';
import { MatchDetailTabId } from '../sportsbook.models';
import { getSportPresentation } from '../config/sportsbook-sports.config';
import { normalizeLanguageCode, trimToNull } from './sportsbook-shared.utils';

export type SportsbookLabelMatch = Pick<Match, 'Hometeam' | 'Awayteam' | 'HometeamMultiLang' | 'AwayteamMultiLang' | 'Outright' | 'MatchIdentifier'>;

export type SportsbookTranslator = (key: string) => string;

export interface SportsbookLocalizationContext {
  currentLanguage: string;
  t: SportsbookTranslator;
}

const BET_TYPE_LABEL_KEYS: Partial<Record<BetTypeKey, string>> = {
  [BetTypeKey.WAY_3]: 'sportsbook.betTypes.matchResult',
  [BetTypeKey.WAY_2]: 'sportsbook.betTypes.matchResult',
  [BetTypeKey.TENNIS_MATCH_WINNER]: 'sportsbook.betTypes.matchWinner',
  [BetTypeKey.TENNIS_SET_WINNER]: 'sportsbook.betTypes.setWinner',
  [BetTypeKey.TENNIS_GAMES_WINNER]: 'sportsbook.betTypes.gameWinner',
  [BetTypeKey.TENNIS_RESULTS_BEST3]: 'sportsbook.betTypes.setBetBestOf3',
  [BetTypeKey.TENNIS_RESULTS_BEST5]: 'sportsbook.betTypes.setBetBestOf5',
  [BetTypeKey.TENNIS_SETS_COUNT_3]: 'sportsbook.betTypes.numberOfSets',
  [BetTypeKey.TENNIS_SETS_COUNT_5]: 'sportsbook.betTypes.numberOfSets',
  [BetTypeKey.TENNIS_UNDER_OVER]: 'sportsbook.betTypes.totalGames',
  [BetTypeKey.TENNIS_GAME_X_WINNER]: 'sportsbook.betTypes.gameWinner',
  [BetTypeKey.TENNIS_SET_UNDER_OVER]: 'sportsbook.betTypes.setTotalGames',
  [BetTypeKey.TENNIS_SET_CORRECT_SCORE]: 'sportsbook.betTypes.correctScore',
  [BetTypeKey.REST_OF_MATCH]: 'sportsbook.betTypes.restOfMatch',
  [BetTypeKey.NEXT_GOAL]: 'sportsbook.betTypes.nextGoal',
  [BetTypeKey.GOAL_NOGOAL]: 'sportsbook.betTypes.bothTeamsToScore',
  [BetTypeKey.HANDICAP]: 'sportsbook.betTypes.handicap',
  [BetTypeKey.DOUBLE_CHANCE]: 'sportsbook.betTypes.doubleChance',
  [BetTypeKey.DNB]: 'sportsbook.betTypes.drawNoBet',
  [BetTypeKey.PENALTY_SHOOTOUT]: 'sportsbook.betTypes.penaltyShootout',
  [BetTypeKey.ADVANCE_NEXT_ROUND]: 'sportsbook.betTypes.advanceNextRound',
  [BetTypeKey.CORRECT_SCORE]: 'sportsbook.betTypes.correctScore',
  [BetTypeKey.CORRECT_SCORE_LIVE]: 'sportsbook.betTypes.correctScore',
  [BetTypeKey.TOTAL_GOALS]: 'sportsbook.betTypes.totalGoals',
  [BetTypeKey.TOTAL_GOALS_EXACT]: 'sportsbook.betTypes.totalGoals',
  [BetTypeKey.GOAL_HOMETEAM]: 'sportsbook.betTypes.homeTeamGoals',
  [BetTypeKey.GOAL_AWAYTEAM]: 'sportsbook.betTypes.awayTeamGoals',
  [BetTypeKey.GOAL_ODDEVEN]: 'sportsbook.betTypes.oddEven',
  [BetTypeKey.HIGHEST_SCORE_HALF]: 'sportsbook.betTypes.highestScoringHalf',
  [BetTypeKey.UNDER_OVER_GOAL_NOGOAL]: 'sportsbook.betTypes.bothTeamsToScoreAndTotal',
  [BetTypeKey.WAY_3_INT_10]: 'sportsbook.betTypes.tenMinuteWinner',
  [BetTypeKey.PERIOD_WINNER]: 'sportsbook.betTypes.periodWinner',
  [BetTypeKey.XTH_GOAL_PERIOD]: 'sportsbook.betTypes.whoScores',
  [BetTypeKey.HIGHEST_SCORE_PERIOD]: 'sportsbook.betTypes.highestScoringPeriod',
  [BetTypeKey.CORNER_WAY3]: 'sportsbook.betTypes.cornerResult',
  [BetTypeKey.CORNER_TOTAL]: 'sportsbook.betTypes.cornerTotal',
  [BetTypeKey.HT_FT]: 'sportsbook.betTypes.halfTimeFullTime',
  [BetTypeKey.OUTRIGHT_WINNER]: 'sportsbook.betTypes.outrightWinner',
  [BetTypeKey.WIN_TO_NIL_HOMETEAM]: 'sportsbook.betTypes.winToNilHomeTeam',
  [BetTypeKey.WIN_TO_NIL_AWAYTEAM]: 'sportsbook.betTypes.winToNilAwayTeam',
  [BetTypeKey.WIN_EITHER_HALF_HOMETEAM]: 'sportsbook.betTypes.winEitherHalfHomeTeam',
  [BetTypeKey.WIN_EITHER_HALF_AWAYTEAM]: 'sportsbook.betTypes.winEitherHalfAwayTeam',
  [BetTypeKey.GNG_BOTH_HALVES_HOMETEAM]: 'sportsbook.betTypes.scoreBothHalvesHomeTeam',
  [BetTypeKey.GNG_BOTH_HALVES_AWAYTEAM]: 'sportsbook.betTypes.scoreBothHalvesAwayTeam',
  [BetTypeKey.XTH_GOAL_WAY3]: 'sportsbook.betTypes.xthGoalWay3',
  [BetTypeKey.WAY_3_GNG_H2]: 'sportsbook.betTypes.secondHalfResultBothTeamsToScore',
  [BetTypeKey.WAY_3_UNDER_OVER_H2]: 'sportsbook.betTypes.secondHalfResultTotal',
  [BetTypeKey.MULTISCORES]: 'sportsbook.betTypes.multiscores',
  [BetTypeKey.WAY_3_OR_WAY_3_H1]: 'sportsbook.betTypes.firstHalfOrFullTime',
  [BetTypeKey.WAY_3_XUP]: 'sportsbook.betTypes.instantWinSuffix',
  [BetTypeKey.ANYTIME_GOALSCORER]: 'sportsbook.betTypes.anytimeGoalscorer'
};

function resolveLocalizedFeedValue(
  values: Record<string, string> | null,
  currentLanguage: string
): string | null {
  const requestedLanguage = normalizeLanguageCode(currentLanguage.trim() || 'en');

  for (const [language, value] of Object.entries(values ?? {})) {
    if (normalizeLanguageCode(language) !== requestedLanguage) {
      continue;
    }

    const localizedValue = trimToNull(value);
    if (localizedValue) {
      return localizedValue;
    }
  }

  return null;
}

export function resolveLocalizedOddLabel(odd: Odd, currentLanguage: string): string | null {
  return resolveLocalizedFeedValue(odd.MultiLang, currentLanguage);
}

function resolveLocalizedTeamLabel(
  localizedValues: Record<string, string> | null,
  fallbackValue: string,
  currentLanguage: string
): string {
  return resolveLocalizedFeedValue(localizedValues, currentLanguage) ?? trimToNull(fallbackValue) ?? '';
}

export function resolveHomeTeamLabel(match: SportsbookLabelMatch, currentLanguage: string): string {
  return resolveLocalizedTeamLabel(match.HometeamMultiLang, match.Hometeam, currentLanguage);
}

export function resolveAwayTeamLabel(match: SportsbookLabelMatch, currentLanguage: string): string {
  return resolveLocalizedTeamLabel(match.AwayteamMultiLang, match.Awayteam, currentLanguage);
}

export function resolveOutrightLabel(match: SportsbookLabelMatch, currentLanguage: string): string {
  const competitors = [resolveHomeTeamLabel(match, currentLanguage), resolveAwayTeamLabel(match, currentLanguage)]
    .map(value => value.trim())
    .filter((value): value is string => Boolean(value));

  if (competitors.length) {
    return competitors.join(' / ');
  }

  const identifier = match.MatchIdentifier.trim();
  if (identifier) {
    return identifier;
  }

  return '';
}

export function buildMatchName(match: SportsbookLabelMatch, currentLanguage: string): string {
  if (match.Outright) {
    return resolveOutrightLabel(match, currentLanguage);
  }

  const homeTeam = resolveHomeTeamLabel(match, currentLanguage);
  const awayTeam = resolveAwayTeamLabel(match, currentLanguage);
  return awayTeam ? `${homeTeam} v ${awayTeam}` : homeTeam;
}

export function resolveSportLabel(sportId: number, t: SportsbookTranslator): string {
  const sportKey = Sport[sportId as Sport];
  return typeof sportKey === 'string' ? t(`sports.${sportKey}`) : getSportPresentation(sportId).label;
}

export function resolveStaticLabel(key: string | undefined, fallback: string, t: SportsbookTranslator): string {
  if (!key) {
    return fallback;
  }

  return t(key);
}

export function localizeTabLabel(tabId: MatchDetailTabId, fallback: string, t: SportsbookTranslator): string {
  const definition = MATCH_DETAIL_TAB_DEFINITIONS.find(candidate => candidate.id === tabId);
  return resolveStaticLabel(definition?.labelKey, fallback, t);
}

export function localizeSectionTitle(sectionId: string, fallback: string, t: SportsbookTranslator): string {
  const family = MATCH_DETAIL_SECTION_FAMILIES.find(candidate => candidate.id === sectionId);
  return resolveStaticLabel(family?.titleKey, fallback, t);
}

export function getSectionTitleFallback(sectionId: string): string | null {
  return MATCH_DETAIL_SECTION_FAMILIES.find(candidate => candidate.id === sectionId)?.title ?? null;
}

export function resolveCanonicalBetTypeLabel(
  betTypeKey: BetTypeKey,
  fallback: string,
  t: SportsbookTranslator
): string {
  const labelKey = BET_TYPE_LABEL_KEYS[betTypeKey];
  return labelKey ? resolveStaticLabel(labelKey, fallback, t) : fallback;
}

export function resolveBetTypeLabel(
  match: SportsbookLabelMatch,
  betType: BetType,
  fallback: string,
  context: SportsbookLocalizationContext
): string {
  const firstHalf = context.t('sportsbook.common.firstHalf');
  const secondHalf = context.t('sportsbook.common.secondHalf');
  const overtime = context.t('sportsbook.common.overtime');
  const period = context.t('sportsbook.common.period');
  const homeTeam = resolveHomeTeamLabel(match, context.currentLanguage);
  const awayTeam = resolveAwayTeamLabel(match, context.currentLanguage);
  const totalGoalsWithOverUnder = resolveStaticLabel('sportsbook.sections.totalGoals', fallback, context.t);

  switch (betType.Key) {
    case BetTypeKey.WAY_3:
    case BetTypeKey.WAY_2:
      return resolveCanonicalBetTypeLabel(BetTypeKey.WAY_3, fallback, context.t);
    case BetTypeKey.TENNIS_MATCH_WINNER:
      return resolveCanonicalBetTypeLabel(BetTypeKey.TENNIS_MATCH_WINNER, fallback, context.t);
    case BetTypeKey.TENNIS_SET_WINNER:
      return resolveCanonicalBetTypeLabel(BetTypeKey.TENNIS_SET_WINNER, fallback, context.t);
    case BetTypeKey.REST_OF_MATCH:
      return resolveCanonicalBetTypeLabel(BetTypeKey.REST_OF_MATCH, fallback, context.t);
    case BetTypeKey.REST_OF_MATCH_H1:
      return `${firstHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.REST_OF_MATCH, fallback, context.t)}`;
    case BetTypeKey.REST_OF_MATCH_OT:
      return `${overtime} - ${resolveCanonicalBetTypeLabel(BetTypeKey.REST_OF_MATCH, fallback, context.t)}`;
    case BetTypeKey.NEXT_GOAL:
      return resolveCanonicalBetTypeLabel(BetTypeKey.NEXT_GOAL, fallback, context.t);
    case BetTypeKey.NEXT_GOAL_H1:
      return `${firstHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.NEXT_GOAL, fallback, context.t)}`;
    case BetTypeKey.NEXT_GOAL_OT:
      return `${overtime} - ${resolveCanonicalBetTypeLabel(BetTypeKey.NEXT_GOAL, fallback, context.t)}`;
    case BetTypeKey.GOAL_NOGOAL:
      return resolveCanonicalBetTypeLabel(BetTypeKey.GOAL_NOGOAL, fallback, context.t);
    case BetTypeKey.GOAL_NOGOAL_H1:
      return `${firstHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.GOAL_NOGOAL, fallback, context.t)}`;
    case BetTypeKey.GOAL_NOGOAL_H2:
      return `${secondHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.GOAL_NOGOAL, fallback, context.t)}`;
    case BetTypeKey.WAY_3_GOAL_NOGOAL:
      return `${resolveCanonicalBetTypeLabel(BetTypeKey.WAY_3, fallback, context.t)} + ${resolveCanonicalBetTypeLabel(BetTypeKey.GOAL_NOGOAL, fallback, context.t)}`;
    case BetTypeKey.WAY_3_GOAL_NOGOAL_H1:
      return `${firstHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.WAY_3, fallback, context.t)} + ${resolveCanonicalBetTypeLabel(BetTypeKey.GOAL_NOGOAL, fallback, context.t)}`;
    case BetTypeKey.DOUBLE_CHANCE_GOAL_NOGOAL:
      return `${resolveCanonicalBetTypeLabel(BetTypeKey.DOUBLE_CHANCE, fallback, context.t)} + ${resolveCanonicalBetTypeLabel(BetTypeKey.GOAL_NOGOAL, fallback, context.t)}`;
    case BetTypeKey.HANDICAP:
      return resolveCanonicalBetTypeLabel(BetTypeKey.HANDICAP, fallback, context.t);
    case BetTypeKey.HANDICAP_H1:
      return `${firstHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.HANDICAP, fallback, context.t)}`;
    case BetTypeKey.HANDICAP_H2:
      return `${secondHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.HANDICAP, fallback, context.t)}`;
    case BetTypeKey.DOUBLE_CHANCE:
      return resolveCanonicalBetTypeLabel(BetTypeKey.DOUBLE_CHANCE, fallback, context.t);
    case BetTypeKey.DOUBLE_CHANCE_H1:
      return `${firstHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.DOUBLE_CHANCE, fallback, context.t)}`;
    case BetTypeKey.DOUBLE_CHANCE_H2:
      return `${secondHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.DOUBLE_CHANCE, fallback, context.t)}`;
    case BetTypeKey.DNB:
      return resolveCanonicalBetTypeLabel(BetTypeKey.DNB, fallback, context.t);
    case BetTypeKey.PENALTY_SHOOTOUT:
      return resolveCanonicalBetTypeLabel(BetTypeKey.PENALTY_SHOOTOUT, fallback, context.t);
    case BetTypeKey.ADVANCE_NEXT_ROUND:
      return resolveCanonicalBetTypeLabel(BetTypeKey.ADVANCE_NEXT_ROUND, fallback, context.t);
    case BetTypeKey.CORRECT_SCORE:
    case BetTypeKey.CORRECT_SCORE_LIVE:
      return resolveCanonicalBetTypeLabel(BetTypeKey.CORRECT_SCORE, fallback, context.t);
    case BetTypeKey.CORRECT_SCORE_H1:
    case BetTypeKey.CORRECT_SCORE_LIVE_H1:
      return `${firstHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.CORRECT_SCORE, fallback, context.t)}`;
    case BetTypeKey.TOTAL_GOALS:
    case BetTypeKey.TOTAL_GOALS_EXACT:
      return resolveCanonicalBetTypeLabel(BetTypeKey.TOTAL_GOALS, fallback, context.t);
    case BetTypeKey.TOTAL_GOALS_EXACT_H1:
      return `${firstHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.TOTAL_GOALS, fallback, context.t)}`;
    case BetTypeKey.TOTAL_GOALS_EXACT_H2:
      return `${secondHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.TOTAL_GOALS, fallback, context.t)}`;
    case BetTypeKey.UNDER_OVER_GOAL_NOGOAL:
      return `${resolveCanonicalBetTypeLabel(betType.Key, fallback, context.t)} ${betType.SpecialOddsValue}`.trim();
    case BetTypeKey.WAY_3_UNDER_OVER:
      return `${resolveCanonicalBetTypeLabel(BetTypeKey.WAY_3, fallback, context.t)} + ${totalGoalsWithOverUnder}`;
    case BetTypeKey.WAY_3_UNDER_OVER_H1:
      return `${firstHalf} - ${resolveCanonicalBetTypeLabel(BetTypeKey.WAY_3, fallback, context.t)} + ${totalGoalsWithOverUnder}`;
    case BetTypeKey.DOUBLE_CHANCE_UNDER_OVER:
      return `${resolveCanonicalBetTypeLabel(BetTypeKey.DOUBLE_CHANCE, fallback, context.t)} + ${totalGoalsWithOverUnder}`;
    case BetTypeKey.WAY_3_INT_10:
      return `${resolveCanonicalBetTypeLabel(betType.Key, fallback, context.t)} ${betType.SpecialOddsValue.replace('-', '/')}`.trim();
    case BetTypeKey.PERIOD_WINNER:
      return resolveCanonicalBetTypeLabel(BetTypeKey.PERIOD_WINNER, fallback, context.t);
    case BetTypeKey.UNDER_OVER_PERIOD:
      return `${period} - ${resolveCanonicalBetTypeLabel(BetTypeKey.TOTAL_GOALS, fallback, context.t)}`;
    case BetTypeKey.REST_OF_MATCH_PERIOD:
      return `${period} - ${resolveCanonicalBetTypeLabel(BetTypeKey.REST_OF_MATCH, fallback, context.t)}`;
    case BetTypeKey.XTH_GOAL_PERIOD:
      return resolveCanonicalBetTypeLabel(BetTypeKey.XTH_GOAL_PERIOD, fallback, context.t);
    case BetTypeKey.HIGHEST_SCORE_PERIOD:
      return resolveCanonicalBetTypeLabel(BetTypeKey.HIGHEST_SCORE_PERIOD, fallback, context.t);
    case BetTypeKey.UNDER_OVER_HOMETEAM:
      return `${homeTeam} - ${totalGoalsWithOverUnder}`;
    case BetTypeKey.UNDER_OVER_HOMETEAM_H1:
      return `${firstHalf} - ${homeTeam} - ${totalGoalsWithOverUnder}`;
    case BetTypeKey.UNDER_OVER_AWAYTEAM:
      return `${awayTeam} - ${totalGoalsWithOverUnder}`;
    case BetTypeKey.UNDER_OVER_AWAYTEAM_H1:
      return `${firstHalf} - ${awayTeam} - ${totalGoalsWithOverUnder}`;
    case BetTypeKey.UNDER_OVER_CORNER:
    case BetTypeKey.UNDER_OVER_CORNER_H1:
      return resolveStaticLabel('sportsbook.sections.corners', fallback, context.t);
    case BetTypeKey.UNDER_OVER_CORNER_HOMETEAM:
      return `${homeTeam} - ${resolveStaticLabel('sportsbook.sections.corners', fallback, context.t)}`;
    case BetTypeKey.UNDER_OVER_CORNER_AWAYTEAM:
      return `${awayTeam} - ${resolveStaticLabel('sportsbook.sections.corners', fallback, context.t)}`;
    case BetTypeKey.CORNER_WAY3:
      return resolveCanonicalBetTypeLabel(BetTypeKey.CORNER_WAY3, fallback, context.t);
    case BetTypeKey.CORNER_TOTAL:
      return resolveCanonicalBetTypeLabel(BetTypeKey.CORNER_TOTAL, fallback, context.t);
    case BetTypeKey.HT_FT:
      return resolveCanonicalBetTypeLabel(BetTypeKey.HT_FT, fallback, context.t);
    case BetTypeKey.OUTRIGHT_WINNER:
      return resolveCanonicalBetTypeLabel(BetTypeKey.OUTRIGHT_WINNER, fallback, context.t);
    case BetTypeKey.WIN_TO_NIL_HOMETEAM:
      return resolveCanonicalBetTypeLabel(BetTypeKey.WIN_TO_NIL_HOMETEAM, fallback, context.t);
    case BetTypeKey.WIN_TO_NIL_AWAYTEAM:
      return resolveCanonicalBetTypeLabel(BetTypeKey.WIN_TO_NIL_AWAYTEAM, fallback, context.t);
    case BetTypeKey.WIN_EITHER_HALF_HOMETEAM:
      return resolveCanonicalBetTypeLabel(BetTypeKey.WIN_EITHER_HALF_HOMETEAM, fallback, context.t);
    case BetTypeKey.WIN_EITHER_HALF_AWAYTEAM:
      return resolveCanonicalBetTypeLabel(BetTypeKey.WIN_EITHER_HALF_AWAYTEAM, fallback, context.t);
    case BetTypeKey.GNG_BOTH_HALVES_HOMETEAM:
      return resolveCanonicalBetTypeLabel(BetTypeKey.GNG_BOTH_HALVES_HOMETEAM, fallback, context.t);
    case BetTypeKey.GNG_BOTH_HALVES_AWAYTEAM:
      return resolveCanonicalBetTypeLabel(BetTypeKey.GNG_BOTH_HALVES_AWAYTEAM, fallback, context.t);
    case BetTypeKey.XTH_GOAL_WAY3:
      return resolveCanonicalBetTypeLabel(BetTypeKey.XTH_GOAL_WAY3, fallback, context.t);
    case BetTypeKey.WAY_3_GNG_H2:
      return resolveCanonicalBetTypeLabel(BetTypeKey.WAY_3_GNG_H2, fallback, context.t);
    case BetTypeKey.WAY_3_UNDER_OVER_H2:
      return resolveCanonicalBetTypeLabel(BetTypeKey.WAY_3_UNDER_OVER_H2, fallback, context.t);
    case BetTypeKey.MULTISCORES:
      return resolveCanonicalBetTypeLabel(BetTypeKey.MULTISCORES, fallback, context.t);
    case BetTypeKey.WAY_3_XUP: {
      const label = resolveCanonicalBetTypeLabel(betType.Key, fallback, context.t);
      return betType.SpecialOddsValue ? `${betType.SpecialOddsValue}-${label}` : label;
    }
    default:
      return resolveCanonicalBetTypeLabel(betType.Key, fallback, context.t);
  }
}
